import axios, { AxiosResponse } from "axios";
import { URLS } from "../constants";
import i18n from "../i18n";
import logger from "../utils/logger";

const axiosInstance = axios.create({
  baseURL: URLS.CLIENT_URL,
  withCredentials: true,
  timeout: 30000,
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["Accept-Language"] = i18n.language;

    // Check for local API key (for local development pointing to remote env)
    const localApiKey = process.env.REACT_APP_LOCAL_API_KEY;
    if (localApiKey) {
      config.headers.Authorization = `Bearer ${localApiKey}`;
      return config;
    }

    // Otherwise use token from localStorage
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        if (userData?.token) {
          config.headers.Authorization = `Bearer ${userData.token}`;
        }
      } catch (e) {
        logger.error("Error parsing authUser data:", e);
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      const currentPath = window.location.pathname;

      // Avoid redirect loop if already on auth pages or refresh endpoint
      if (
        currentPath === "/login" ||
        currentPath === "/register" ||
        currentPath === "/recover-password" ||
        originalRequest.url?.includes("/auth/refresh")
      ) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh token
        await axiosInstance.post("/api/auth/refresh", {});

        isRefreshing = false;
        processQueue(null, null);

        // Retry original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        // Clear auth data
        localStorage.removeItem("authUser");
        localStorage.removeItem("token");

        // Redirect to login
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    if (error.code === "NETWORK_ERROR" || error.message === "Network Error") {
      logger.error(
        "Network error detected. Check if backend is running and accessible.",
      );
    }
    return Promise.reject(error);
  },
);

export interface ApiResponse<T> {
  data: T;
  status: number;
}

export interface AuthResponse {
  companyID?: string | number;
  companyId?: string | number;
  email: string;
  gender: string;
  id: string;
  firstName: string;
  lastName: string;
  companyName: string;
  role: string;
  subscription: string;
  language: string;
  telephone: string;
  token?: string;
}

export interface InitialRegistrationRequest {
  termsAccepted: boolean;
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
}

export const signUp = (payload: {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
  auth_provider: string;
}): Promise<AxiosResponse<ApiResponse<AuthResponse>>> =>
  axiosInstance.post("/api/initial-register", payload);

export const login = (
  email: string,
  password: string,
): Promise<AxiosResponse<ApiResponse<AuthResponse>>> =>
  axiosInstance.post("/api/login", { email, password });

export const recoverPassword = (
  email: string,
): Promise<AxiosResponse<ApiResponse<any>>> =>
  axiosInstance.post("/api/recover-password", { email });

export const resetPassword = (
  newPassword: { password: string },
  token: string,
): Promise<AxiosResponse<ApiResponse<any>>> =>
  axiosInstance.post(`/api/reset-password?token=${token}`, newPassword);

export const completeRegistration = (payload: {
  userId: string;
  companyName: string;
  businessType: string;
  instagram?: string;
  website?: string;
  userPhone?: string;
  preferences?: Record<string, string>;
  codeChallenge?: string;
  redirectUri?: string;
  state?: string;
  port?: string | number;
}): Promise<AxiosResponse<AuthResponse>> =>
  axiosInstance.post("/api/complete-registration", payload);
